import React, { useState, useEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import NewSidebar from "./Sidebar/NewSidebar";
import "./NewappLayout.scss";
import { Snackbar, Alert } from "@mui/material";
import { clearErrors } from "../../Redux/ActionCreators/errorAction";
import { useSelector, useDispatch } from "react-redux";

const AppLayout = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
    window.innerWidth < 767
  );
  const [notification, setNotification] = useState(null);
  const [notificationTrigger, setNotificationTrigger] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarCollapsed((prev) => !prev);
  };
  const error = useSelector((state) => state.error);
  const dispatch = useDispatch();
  const socketRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const intentionalCloseRef = useRef(false);
  const MAX_RECONNECT_ATTEMPTS = 10;

  const snackClosed = () => {
    dispatch(clearErrors());
  };

  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const tenantId = localStorage.getItem('tenentId');
    const baseUrl = process.env.REACT_APP_WS_URL;

    if (!token || !tenantId || !baseUrl) return;

    const connect = () => {
      if (intentionalCloseRef.current) return;

      const wsUrl = `${baseUrl}?token=${encodeURIComponent(token)}`;
      const socket = new WebSocket(wsUrl);
      socketRef.current = socket;

      socket.onopen = () => {
        setWsConnected(true);
        reconnectAttemptsRef.current = 0;
        socket.send(JSON.stringify({ action: 'join', group: `tenant-${tenantId}` }));
      };

      socket.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data);

          if (parsed.type === 'notification') {
            // toast.success("You got a new notification", { autoClose: 3000 });
            setNotificationTrigger(prev => !prev);
          }

          if (parsed.event === 'resume_upload_updated' && parsed.data) {
            window.dispatchEvent(new CustomEvent('resume_upload_updated', { detail: parsed.data }));
          }
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };

      socket.onclose = () => {
        setWsConnected(false);
        if (intentionalCloseRef.current) return;
        if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
          console.warn('WebSocket: max reconnect attempts reached');
          return;
        }
        const delay = Math.min(30000, 1000 * Math.pow(2, reconnectAttemptsRef.current));
        console.warn(`WebSocket disconnected. Reconnecting in ${delay / 1000}s… (attempt ${reconnectAttemptsRef.current + 1})`);
        reconnectAttemptsRef.current += 1;
        reconnectTimerRef.current = setTimeout(connect, delay);
      };

      socket.onerror = () => {
        setWsConnected(false);
        socket.close();
      };
    };

    connect();

    return () => {
      intentionalCloseRef.current = true;
      clearTimeout(reconnectTimerRef.current);
      if (socketRef.current) socketRef.current.close();
    };
  }, []);

  const disconnectWs = () => {
    intentionalCloseRef.current = true;
    clearTimeout(reconnectTimerRef.current);
    if (socketRef.current) socketRef.current.close();
  };

  const isMobile = window.innerWidth < 767;
  const isCreateJobRoute = location.pathname === '/jobs/createjob';
  const isBillingRoute = location.pathname === '/billing/success' || location.pathname === '/billing/cancel';

  const [keyboardOpen, setKeyboardOpen] = useState(false);

  useEffect(() => {
    if (!isMobile || typeof window.visualViewport === 'undefined') return;
    const handler = () => {
      const heightDiff = window.innerHeight - window.visualViewport.height;
      setKeyboardOpen(heightDiff > 100);
    };
    window.visualViewport.addEventListener('resize', handler);
    return () => window.visualViewport.removeEventListener('resize', handler);
  }, [isMobile]);

  return (
    <div className="app-layout">
      {!isMobile && !isBillingRoute && (
      <NewSidebar
        isCollapsed={isSidebarCollapsed}
        toggleSidebar={toggleSidebar}
        wsConnected={wsConnected}
        disconnectWs={disconnectWs}
      />
      )}
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        open={error.successMessage}
        autoHideDuration={3000}
        onClose={snackClosed}
      >
        <Alert onClose={snackClosed} severity="error" sx={{ width: "100%" }}>
          {error.successMessageText}
        </Alert>
      </Snackbar>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        open={error.successEnable}
        onClose={() => snackClosed()}
        autoHideDuration={3000}
      >
        <Alert onClose={snackClosed} severity="success" sx={{ width: "100%" }}>
          {error.successMessageText}
        </Alert>
      </Snackbar>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        open={Boolean(notification)}
        autoHideDuration={3000}
        onClose={() => setNotification(null)}
      >
        <Alert onClose={() => setNotification(null)} severity="info" sx={{ width: '100%' }}>
          {notification}
        </Alert>
      </Snackbar>
      <div className="main-content">
        <div className="page-content">
          <Outlet context={{ notificationTrigger }} />
        </div>
        {isMobile && !isCreateJobRoute && !isBillingRoute && !keyboardOpen && (
          <div className="mobile-sidebar">
            <NewSidebar
              isCollapsed={false}
              toggleSidebar={toggleSidebar}
              wsConnected={wsConnected}
              disconnectWs={disconnectWs}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AppLayout;
